const AI_ENRICHMENT_BASE_URL = process.env.REACT_APP_AI_ENRICHMENT_BASE_URL;
const SF_UPLOAD_BASE_URL = process.env.REACT_APP_SF_UPLOAD_BASE_URL;
console.log("AI_ENRICHMENT_BASE_URL =", AI_ENRICHMENT_BASE_URL);
console.log("SF_UPLOAD_BASE_URL =", SF_UPLOAD_BASE_URL);

// ============================================================
// 1st UI — AI Enrichment Backend (AI_ENRICHMENT_BASE_URL)
// ============================================================

export const uploadFiles = async (files, passwords = {}) => {
  const formData = new FormData();

  files.forEach(file => {
    formData.append('files', file);
  });

  if (Object.keys(passwords).length > 0) {
    formData.append('passwords', JSON.stringify(passwords));
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    const response = await fetch(`${AI_ENRICHMENT_BASE_URL}/api/upload`, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Upload failed');
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Upload timeout - please try again');
    }
    console.error('Upload error:', error);
    throw error;
  }
};

// For 1st UI — password check hits AI_ENRICHMENT_BASE_URL
export const checkPasswordProtectionAI = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(`${AI_ENRICHMENT_BASE_URL}/api/check-password`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) throw new Error('Failed to check password protection');
    return await response.json();
  } catch (error) {
    console.error('Password check error (AI):', error);
    throw error;
  }
};

// For 1st UI — password verify hits AI_ENRICHMENT_BASE_URL
export const verifyPasswordAI = async (file, password) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('password', password);

  try {
    const response = await fetch(`${AI_ENRICHMENT_BASE_URL}/api/verify-password`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) throw new Error('Failed to verify password');
    return await response.json();
  } catch (error) {
    console.error('Password verification error (AI):', error);
    throw error;
  }
};

// ============================================================
// 2nd UI — Salesforce Backend (SF_UPLOAD_BASE_URL)
// ============================================================

export const sfuploadFiles = async (file, opportunityId, sfUserEmail = "") => {
  if (!file) throw new Error("No file provided");
  if (!opportunityId) throw new Error("Opportunity ID is required");

  const formData = new FormData();
  formData.append("file", file);
  formData.append("opportunity_id", opportunityId);
  formData.append("sf_user_email", sfUserEmail);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    const response = await fetch(`${AI_ENRICHMENT_BASE_URL}/api/sfupload`, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Upload failed");
    }

    return await response.json();
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("Upload timeout - please try again");
    }
    console.error("Upload error:", error);
    throw error;
  }
};

// For 2nd UI — password check hits SF_UPLOAD_BASE_URL
export const checkPasswordProtection = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(`${SF_UPLOAD_BASE_URL}/api/sfupload/check-password`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) throw new Error('Failed to check password protection');
    return await response.json();
  } catch (error) {
    console.error('Password check error (SF):', error);
    throw error;
  }
};

// For 2nd UI — password verify hits SF_UPLOAD_BASE_URL
export const verifyPassword = async (file, password) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('password', password);

  try {
    const response = await fetch(`${SF_UPLOAD_BASE_URL}/api/sfupload/verify-password`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) throw new Error('Failed to verify password');
    return await response.json();
  } catch (error) {
    console.error('Password verification error (SF):', error);
    throw error;
  }
};

// ============================================================
// Shared — Job Status & Polling (AI_ENRICHMENT_BASE_URL)
// ============================================================

export const getJobStatus = async (jobId, retryCount = 0) => {
  const MAX_RETRIES = 3;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    const response = await fetch(`${AI_ENRICHMENT_BASE_URL}/api/job/${jobId}`, {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.status === 409) {
      if (retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        return getJobStatus(jobId, retryCount + 1);
      }
      throw new Error('Job is still initializing. Please try again in a moment.');
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to get job status');
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      if (retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        return getJobStatus(jobId, retryCount + 1);
      }
      throw new Error('Job status check timeout');
    }
    console.error('Job status error:', error);
    throw error;
  }
};

export const pollJobUntilComplete = async (jobId, onProgress) => {
  const maxAttempts = 1200;
  const minPollInterval = 2000;
  const maxPollInterval = 10000;
  let consecutiveErrors = 0;
  const MAX_CONSECUTIVE_ERRORS = 5;

  const poll = async (attempts, currentInterval) => {
    if (attempts >= maxAttempts) {
      throw new Error('Job timeout: Processing took too long (> 1 hour)');
    }

    try {
      const status = await getJobStatus(jobId);

      if (onProgress) onProgress(status);

      if (status.status === 'completed') {
        consecutiveErrors = 0;
        return status;
      }

      if (status.status === 'error') {
        consecutiveErrors++;
        console.warn(`Job ${jobId} reported error (attempts: ${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS})`);
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          console.error(`Job ${jobId} reached consecutive error limit; returning error status.`);
          return status;
        }
        await new Promise(resolve => setTimeout(resolve, currentInterval * 1.5));
        return poll(attempts + 1, Math.min(currentInterval * 1.5, maxPollInterval));
      }

      consecutiveErrors = 0;

      let nextInterval = currentInterval;
      if (status.current_step === 'queued') {
        nextInterval = minPollInterval;
      } else {
        nextInterval = Math.min(currentInterval * 1.2, maxPollInterval);
      }

      await new Promise(resolve => setTimeout(resolve, nextInterval));
      return poll(attempts + 1, nextInterval);

    } catch (error) {
      consecutiveErrors++;
      console.warn(`Job ${jobId} polling error (attempts: ${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error);

      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        throw new Error('Job polling failed after multiple retries');
      }

      await new Promise(resolve => setTimeout(resolve, currentInterval * 2));
      return poll(attempts + 1, Math.min(currentInterval * 1.5, maxPollInterval));
    }
  };

  return poll(0, minPollInterval);
};

// ============================================================
// Shared — Downloads (AI_ENRICHMENT_BASE_URL)
// ============================================================

export const downloadCleanedFile = async (filename) => {
  try {
    const response = await fetch(
      `${AI_ENRICHMENT_BASE_URL}/api/download/cleaned?filename=${encodeURIComponent(filename)}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

export const downloadEnrichedFile = async (filename) => {
  try {
    const response = await fetch(
      `${AI_ENRICHMENT_BASE_URL}/api/download/enriched?filename=${encodeURIComponent(filename)}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  } catch (error) {
    console.error('Download enriched file error:', error);
    throw error;
  }
};

// ============================================================
// Shared — Health & Recent Jobs (AI_ENRICHMENT_BASE_URL)
// ============================================================

export const healthCheck = async () => {
  try {
    const response = await fetch(`${AI_ENRICHMENT_BASE_URL}/api/health`);
    return await response.json();
  } catch (error) {
    console.error('Health check error:', error);
    throw error;
  }
};

export const getRecentJobs = async (limit = 10, status = null) => {
  try {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (status) params.append('status', status);

    const response = await fetch(
      `${AI_ENRICHMENT_BASE_URL}/api/jobs/recent?${params.toString()}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to fetch recent jobs');
    }

    return await response.json();
  } catch (error) {
    console.error('Get recent jobs error:', error);
    throw error;
  }
};